// Calendar (MAPA 35-R4, 22.09): a month of dots, an account switcher, the day window.
// Keep the keys in step with pl/appCalendar.ts.
//
// Copy frame: the calendar speaks ONLY about what is scheduled and what has already left
// our side — never about what happens to a post after it is out. A post's state is a pill
// with one word; a scheduled post carries no pill (rules 5 and 30). No explaining
// sentences (rule 8): a sentence stands only in a decision window, on an error and as
// a legal requirement.
export const appCalendar = {
  topbar: {
    // The screen title is „Publishing” (the tab frame, session 35) — this key only names
    // the view for a screen reader.
    title: 'Calendar',
    plan: 'Schedule publication',
  },
  // Rule 24: the state of the WHOLE feature as a pill in the screen title row.
  feature: {
    paused: 'publishing paused',
  },
  // The month row: „today” and the arrows. `prevWeek` / `nextWeek` still serve the
  // Planner's week strip (`MonthCalendar`, `rail` variant) and die with it.
  period: {
    today: 'today',
    prevMonth: 'Previous month',
    nextMonth: 'Next month',
    prevWeek: 'Previous week',
    nextWeek: 'Next week',
  },
  grid: {
    label: 'Month day by day',
    // Weekday initials above the dot grid (from Monday), mono 11 (MAPA 35-R4).
    weekdays: 'Mo,Tu,We,Th,Fr,Sa,Su',
    // The Planner's week strip (`rail` variant); the Calendar has no week any more.
    weekLabel: 'Week day by day',
    // The cell's name for a screen reader instead of a legend: „Tue 22 Sep, 6 posts”.
    dayWithPosts: '{date}, {posts}',
    // Appended to the name of today's cell (round 43-A): the time is data under the dots.
    todayNext: 'today, next post at {time}',
    // Move mode (round 43-C): the account's free places that day — a cell name for the reader.
    // Three forms like Polish, because the call picks the suffix (few = many in English).
    freePlaces_one: '{n} free place',
    freePlaces_few: '{n} free places',
    freePlaces_many: '{n} free places',
    noFreePlaces: 'no free places',
    // Accounts beyond the three rows of a cell on a computer.
    moreAccounts_one: '+{n} account',
    moreAccounts_few: '+{n} accounts',
    moreAccounts_many: '+{n} accounts',
  },
  // The account switcher (a sheet on a phone, a 304 px menu on a computer).
  accounts: {
    all: 'All accounts',
    title: 'Account',
    // The name of the signal dot by the switcher.
    reauthDot: 'an account needs signing in again',
  },
  // One action line under the grid (the morning line, round 43-A): the counters take the
  // state words from `status.*`, and „Approve” carries no number.
  action: {
    review: 'Approve',
    // Names of the counter-filter for the reader (`?stan=`); on screen the state word and number.
    filterState: 'Show only: {status}',
    filterOff: 'Show all statuses',
  },
  day: {
    // The day list under the Planner's week strip (`rail` variant).
    none: 'No posts on this day',
    posts_one: '{n} post',
    posts_few: '{n} posts',
    posts_many: '{n} posts',
    // The day window's title on the content level: „@nocna.zmiana · Tue 22 Sep”.
    accountTitle: '{account} · {date}',
    back: 'Back to accounts',
    // Today's day (round 43-A): the word by the now line (the time is data) and the name of
    // the next time in an account row — for the reader only, on screen just the time.
    now: 'now',
    nextAt: 'next {time}',
  },
  post: {
    expand: 'Expand description',
    collapse: 'Collapse description',
    editCaption: 'Edit description',
    remove: 'Remove from plan',
    // The verb alone; the amount component adds the price (rules 46 and 48).
    retry: 'Retry',
    // Moving a post to another date (round 43-C); only with the server's `can_move`.
    move: 'Move',
    open: 'Open on {platformLoc}',
    approve: 'Approve',
    // A post of a plan nobody has started: the server refuses a consent from the day
    // window (409 `campaign_not_active`), so the way leads to approving the PLAN (KB-03).
    approvePlan: 'Approve the plan',
    // Why a post failed: ONE word or two, never a sentence. Key = the server's error
    // class (`publish_job.error_class`); an unknown class → `unknown`.
    reason: {
      platform_cap: 'platform limit',
      daily_cap: 'daily limit',
      min_gap: 'spacing',
      past_due: 'invoice',
      connection_expired: 'account disconnected',
      connection_suspended: 'account outside plan',
      privacy_unavailable: 'visibility',
      rate_limited: 'pace',
      auth_removed: 'access revoked',
      provider_timeout: 'no answer',
      content_gate: 'content',
      provider_quota: 'capacity',
      render_too_large: 'file too large',
      withdrawal_failed: 'withdrawal',
      feature_disabled: 'publishing off',
      deferral_expired: 'past due date',
      provider_rejected: 'rejected',
      // The classes the server writes most often (`dispatcher.classify_error`,
      // `reconcile`); without them the commonest failure said „checking” (review G, KF-05).
      provider_unavailable: 'provider unavailable',
      rejected: 'rejected',
      timeout: 'no answer',
      consent_missing: 'no consent',
      gems_on_hold: 'settlement pending',
      still_in_flight: 'sending',
      day_full: 'day full',
      unknown: 'checking',
    },
  },
  // Post state pills; a scheduled post has none.
  status: {
    published: 'published',
    awaiting: 'awaiting approval',
    failed: 'failed',
    cancelled: 'cancelled',
    // `scheduled` is NOT a pill (rules 5 and 30): the word exists only for the screen
    // reader, inside a month cell’s name (review G, KF-12).
    scheduled: 'scheduled',
  },
  caption: {
    // The counter is DATA, not a sentence.
    counter: '{n} / {max}',
    // The small button under the description field: the variables THIS post can fill.
    vars: '{ } Variables',
    // TikTok's legal requirement (protected zone): a sentence above the footer instead of
    // a checkbox (coordinator decision 2). Both links are the composer's.
    legalTiktok: 'Saving confirms you hold the rights to the music and content of this post under {music} and {branded}.',
    legalMusic: "TikTok's Music Usage Confirmation",
    legalBranded: 'the Branded Content Policy',
    save: 'Save and confirm',
    saveOnly: 'Save description',
    cancel: 'Cancel',
    // A content gate refusal: one sentence + a code (rule 8).
    rejected: "This description can't be saved.",
    exitTitle: 'Leave without saving?',
    exitStay: 'Stay',
    exitLeave: 'Leave',
  },
  remove: {
    title: 'Remove from plan?',
    body: 'The post will not go out; the edit stays in Edits.',
    when: 'Date',
    account: 'Account',
    refund: 'Tokens returned',
    cancel: 'Cancel',
  },
  // Move mode and the hour sheet (round 43-C): the state pill, data, names for the reader.
  move: {
    state: 'moving',
    cancel: 'Cancel',
    cancelSr: 'Cancel move',
    region: 'Moving a post',
    hour: 'Time',
    used: '{used} / {cap}',
    newDate: 'New date',
    submit: 'Move',
    // One-tap fix from `next_free_at` / `earliest_at` (rule 56).
    fix: 'Use {when}',
    slotFree: '{time}',
    slotTaken: '{time}, unavailable',
    // A swap with a post of the same account (frame 43-C-04).
    swap: 'Swap',
    slotSwap: '{time}, {title}, swap',
    // The pill that opens a minute stepper within the plan's window (rule 54).
    other: 'Other',
  },
  retry: {
    title: 'Retry post?',
    whenLabel: 'Date',
    when: 'today, next window',
    // The first column of the 7-day strip (round 43-C, frame 05).
    today: 'today',
  },
  // A toast = one fact after an action (rule 7); the amount component adds the price.
  toast: {
    removed: 'Removed from plan',
    captionSaved: 'Description saved.',
    retried: 'Post retried',
    // The date from the server's answer (a retry may shift it in a race); no date → `retried`.
    retriedAt: 'Post retried · {date}',
    removeFailed: "Couldn't remove the post from the plan.",
    retryFailed: "Couldn't retry the post.",
    saveFailed: "Couldn't save the description.",
    // A move charges nothing: the toast carries only the date from the server's answer.
    moved: 'Moved · {date}',
    swapped: 'Swapped · {a} and {b}',
    moveFailed: "Couldn't move the post.",
  },
  // The server's answer has a ceiling: a pill as data, the full sentence for the reader.
  truncated: {
    shown: 'shown through {date}',
    full: 'This month has more posts than one answer holds: showing posts through {date}. Later days may be incomplete.',
  },
  // An empty month: glyph, title and ONE action (rule 8).
  empty: {
    title: 'No posts this month',
  },
  // A failed read: one sentence, a way back, a code (`kalendarz-{status}`).
  error: {
    title: "Couldn't load the calendar.",
    retry: 'Try again',
  },
  noAccounts: {
    title: 'No connected accounts',
    action: 'Connect an account',
  },
  // „Feature off” has ONE sentence on all four Publishing tabs: `appCampaigns.disabled` (PROD1 FR-4).
}
